// GtBenchExtras — the pieces a bench uses beyond its table: the activity
// panel (with the imports that landed on it), finding people who look like
// the same person, and picking a client before sharing a shortlist.
// Nothing here contacts anyone. Putting someone back restores them on this
// bench exactly as they were when they were taken off.
import { useState, useEffect, useCallback } from 'react';
import { ClientField } from './GtTaxonomyFields';

const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
const words = (v) => (v ? String(v).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '');

function initials(name) { return String(name || '?').trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B', '#2E7D32', '#6A1B9A', '#B8860B', '#00695C', '#C2410C', '#374151', '#9D174D', '#1565C0', '#4E342E', '#5B6C1E', '#0F766E', '#B91C1C', '#5D4037', '#3949AB', '#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }

function dayLabel(iso) {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const opts = { weekday: 'short', day: 'numeric', month: 'short' };
  if (d.getFullYear() !== today.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-GB', opts);
}
function timeOf(iso) { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
function shortDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// People-level actions done in one go are shown as one line.
const GROUPED = ['bench_member_added', 'bench_member_removed', 'bench_member_restored', 'bench_to_role'];
function groupEvents(events) {
  const out = [];
  for (const e of events) {
    const last = out[out.length - 1];
    const prev = last && last.items[last.items.length - 1];
    if (last && GROUPED.indexOf(e.action) !== -1 && last.action === e.action && last.actor === (e.actor_name || '')
      && Math.abs(new Date(prev.occurred_at) - new Date(e.occurred_at)) < 5 * 60000
      && (e.action !== 'bench_to_role' || (prev.detail || {}).role_ref === (e.detail || {}).role_ref)) {
      last.items.push(e);
      continue;
    }
    out.push({ key: e.id, action: e.action, actor: e.actor_name || '', at: e.occurred_at, items: [e] });
  }
  return out;
}

// Everyone a line is about: one event per person, or a move that lists them.
function peopleOf(g) {
  if (g.action === 'bench_members_sorted') {
    const p = (g.items[0].detail || {}).people;
    return Array.isArray(p) ? p.map((x) => x.name || 'someone') : [];
  }
  return GROUPED.indexOf(g.action) !== -1 ? g.items.map((i) => i.candidate_name || 'someone') : [];
}

function namesOf(items) {
  const names = items.map((i) => i.candidate_name || 'someone');
  return listNames(names);
}
function listNames(names) {
  if (names.length === 1) return names[0];
  if (names.length <= 3) return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  return plural(names.length, 'person', 'people');
}

function sentence(g, bucketLabel) {
  const first = g.items[0];
  const d = first.detail || {};
  switch (g.action) {
    case 'bench_created': return 'created this bench' + (d.copied_from ? ', with buckets copied from ' + d.copied_from : '');
    case 'bench_updated': return 'edited the bench';
    case 'bench_archived': return 'archived the bench';
    case 'bench_restored': return 'restored the bench';
    case 'bench_buckets_changed': return 'changed the buckets';
    case 'bench_columns_changed': return 'changed the columns';
    case 'bench_member_added': {
      const src = d.source === 'import' ? ' from an import' : d.source === 'sourcing' ? ' from suggestions' : d.source === 'role' ? ' from a role' : '';
      return 'added ' + namesOf(g.items) + src;
    }
    case 'bench_member_removed': return 'took ' + namesOf(g.items) + ' off the bench';
    case 'bench_member_restored': return 'put ' + namesOf(g.items) + ' back on the bench';
    case 'bench_members_sorted': {
      const n = d.count == null ? null : Number(d.count);
      const named = peopleOf(g);
      const who = named.length && named.length === n ? listNames(named) : n == null ? 'people' : plural(n, 'person', 'people');
      if (d.stage_id == null) return 'moved ' + who + ' out of their bucket';
      return 'moved ' + who + ' to ' + (d.stage || bucketLabel(d.stage_id) || 'a bucket');
    }
    case 'bench_ai_column_run': return 'asked an AI column' + (d.label ? ' (' + d.label + ')' : '') + (d.count ? ' for ' + plural(Number(d.count), 'person', 'people') : '');
    case 'bench_to_role': return 'added ' + namesOf(g.items) + ' to ' + (d.role_ref || 'a role') + (d.stage ? ' at ' + d.stage : '');
    case 'import_completed': {
      const bits = [];
      if (d.created) bits.push(plural(d.created, 'new person', 'new people'));
      if (d.matched) bits.push(d.matched + ' already in the pool');
      return 'imported ' + (d.filename || 'a file') + (bits.length ? ': ' + bits.join(', ') : '');
    }
    default: return words(g.action).toLowerCase();
  }
}

// ── Activity and imports, in a panel on the right ──────────────────────────
export function GtBenchActivity({ bench, buckets, authFetch, flash, onClose, onRestored, onSelect, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'activity');
  const [events, setEvents] = useState(null);
  const [more, setMore] = useState(false);
  const [imports, setImports] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);

  const loadEvents = useCallback(async (before) => {
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/activity' + (before ? '?before=' + before : ''));
      setEvents((cur) => (before && cur ? cur.concat(r.events || []) : (r.events || [])));
      setMore(!!r.more);
    } catch (e) { flash(e.message, true); setEvents((cur) => cur || []); }
  }, [authFetch, bench.id, flash]);

  const loadImports = useCallback(async () => {
    try { const r = await authFetch('/api/benches/' + bench.id + '/imports'); setImports(r.imports || []); }
    catch (e) { flash(e.message, true); setImports([]); }
  }, [authFetch, bench.id, flash]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { if (tab === 'imports' && imports === null) loadImports(); }, [tab, imports, loadImports]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const bucketLabel = (id) => ((buckets || []).find((b) => b.id === id) || {}).label;

  async function putBack(g) {
    const ids = g.items.filter((i) => i.can_restore).map((i) => i.candidate_id);
    if (!ids.length) return;
    setBusy(g.key);
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/members/restore', { method: 'POST', body: JSON.stringify({ candidate_ids: ids }) });
      flash(plural(r.restored, 'person', 'people') + ' put back on the bench.');
      await loadEvents();
      onRestored();
    } catch (e) { flash(e.message, true); }
    setBusy(null);
  }

  async function selectImport(imp) {
    setBusy('i' + imp.id);
    try {
      const r = await authFetch('/api/benches/' + bench.id + '/imports/' + imp.id + '/members');
      onSelect(r.member_ids || [], imp);
    } catch (e) { flash(e.message, true); }
    setBusy(null);
  }

  const groups = events ? groupEvents(events) : [];
  let lastDay = null;
  const archived = bench.status === 'archived';

  return (
    <div className="gtbx-scrim" onClick={onClose}>
      <aside className="gtbx-drawer" role="dialog" aria-label="Bench activity" onClick={(e) => e.stopPropagation()}>
        <div className="gtbx-drawer-head">
          <div>
            <div className="gtbx-kicker">{bench.ref}</div>
            <h2>{bench.name}</h2>
          </div>
          <button type="button" className="gtbx-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="gtbx-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'activity'} className={'gtbx-tab' + (tab === 'activity' ? ' gtbx-tab--on' : '')} onClick={() => setTab('activity')}>Activity</button>
          <button type="button" role="tab" aria-selected={tab === 'imports'} className={'gtbx-tab' + (tab === 'imports' ? ' gtbx-tab--on' : '')} onClick={() => setTab('imports')}>Imports</button>
        </div>

        <div className="gtbx-drawer-body">
          {tab === 'activity' && (
            events === null ? <div className="gtbx-empty">Loading…</div>
              : groups.length === 0 ? <div className="gtbx-empty">Nothing has happened on this bench yet.</div>
                : (
                  <ol className="gtbx-feed">
                    {groups.map((g) => {
                      const day = dayLabel(g.at);
                      const showDay = day !== lastDay;
                      lastDay = day;
                      const restorable = g.action === 'bench_member_removed' ? g.items.filter((i) => i.can_restore).length : 0;
                      const people = peopleOf(g);
                      const many = people.length > 3;
                      return (
                        <li key={g.key} className="gtbx-feed-li">
                          {showDay && <div className="gtbx-day">{day}</div>}
                          <div className={'gtbx-ev gtbx-ev--' + g.action.replace(/^bench_/, '').replace(/_/g, '-')}>
                            <span className="gtbx-dot" aria-hidden="true" />
                            <div className="gtbx-ev-main">
                              <div className="gtbx-ev-line"><b>{g.actor || 'Someone'}</b> {sentence(g, bucketLabel)}</div>
                              {many && (
                                <button type="button" className="gtbx-link" onClick={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))}>
                                  {open[g.key] ? 'Hide names' : 'Show names'}
                                </button>
                              )}
                              {many && open[g.key] && (
                                <div className="gtbx-names">{people.map((nm, i) => <span key={i} className="gtbx-name">{nm}</span>)}</div>
                              )}
                            </div>
                            <div className="gtbx-ev-side">
                              <span className="gtbx-time">{timeOf(g.at)}</span>
                              {restorable > 0 && !archived && (
                                <button type="button" className="gtbx-putback" disabled={busy === g.key} onClick={() => putBack(g)}>
                                  {busy === g.key ? 'Putting back…' : restorable === g.items.length ? 'Put back' : 'Put back ' + restorable}
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )
          )}
          {tab === 'activity' && more && (
            <button type="button" className="gtc-btn-ghost gtbx-more" onClick={() => loadEvents(events[events.length - 1].id)}>Show older</button>
          )}

          {tab === 'imports' && (
            imports === null ? <div className="gtbx-empty">Loading…</div>
              : imports.length === 0 ? <div className="gtbx-empty">No files have been imported onto this bench.</div>
                : (
                  <div className="gtbx-imports">
                    {imports.map((imp) => (
                      <div key={imp.id} className="gtbx-imp">
                        <div className="gtbx-imp-top">
                          <span className="gtbx-imp-ic" aria-hidden="true">CSV</span>
                          <div className="gtbx-imp-title">
                            <b title={imp.filename}>{imp.filename || 'Unnamed file'}</b>
                            <span>{[imp.ref, imp.created_by_name, shortDate(imp.created_at)].filter(Boolean).join(' · ')}</span>
                          </div>
                        </div>
                        <div className="gtbx-imp-stats">
                          <span className="gtbx-stat"><b>{imp.total_rows || 0}</b> rows</span>
                          <span className="gtbx-stat gtbx-stat--new"><b>{imp.imported_rows || 0}</b> new</span>
                          <span className="gtbx-stat"><b>{imp.matched_rows || 0}</b> already in pool</span>
                          {(imp.duplicate_rows || 0) > 0 && <span className="gtbx-stat"><b>{imp.duplicate_rows}</b> repeated</span>}
                          {(imp.skipped_rows || 0) > 0 && <span className="gtbx-stat gtbx-stat--skip"><b>{imp.skipped_rows}</b> skipped</span>}
                        </div>
                        <div className="gtbx-imp-foot">
                          <span>{imp.on_bench ? plural(imp.on_bench, 'person', 'people') + ' from this file on the bench now' : 'Nobody from this file is on the bench now'}</span>
                          {imp.on_bench > 0 && (
                            <button type="button" className="gtc-btn-ghost" disabled={busy === 'i' + imp.id} onClick={() => selectImport(imp)}>
                              {busy === 'i' + imp.id ? 'Finding…' : 'Select them'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
          )}
        </div>
      </aside>
    </div>
  );
}

// ── People who look like the same person ──────────────────────────────────
function normEmail(v) { const s = String(v || '').trim().toLowerCase(); return /@/.test(s) ? s : ''; }
function normLinkedin(v) {
  const s = String(v || '').trim().toLowerCase();
  const m = s.match(/linkedin\.com\/(in|pub)\/([^/?#]+)/);
  return m ? m[2] : '';
}
function normText(v) { return String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim(); }

const REASONS = { email: 'Same email', linkedin: 'Same LinkedIn', github: 'Same GitHub', name: 'Same name and company' };

// Groups of bench members that share an email, a LinkedIn profile, a GitHub
// name, or a full name at the same company. Each group lists why.
export function findDuplicates(members) {
  const parent = {};
  const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const firstBy = {};
  const reasonPairs = [];
  (members || []).forEach((m) => { parent[m.id] = m.id; });
  (members || []).forEach((m) => {
    const name = normText(m.full_name);
    const company = normText(m.current_company);
    const keys = [
      ['email', normEmail(m.email)],
      ['linkedin', normLinkedin(m.linkedin_url)],
      ['github', String(m.github_username || '').trim().toLowerCase()],
      ['name', name && company ? name + '|' + company : ''],
    ];
    keys.forEach(([kind, k]) => {
      if (!k) return;
      const key = kind + ':' + k;
      if (firstBy[key] == null) { firstBy[key] = m.id; return; }
      const a = find(firstBy[key]); const b = find(m.id);
      if (a !== b) parent[b] = a;
      reasonPairs.push([m.id, kind]);
    });
  });
  const groups = {};
  (members || []).forEach((m) => { const r = find(m.id); (groups[r] = groups[r] || []).push(m); });
  const reasonsByRoot = {};
  reasonPairs.forEach(([id, kind]) => { const r = find(id); (reasonsByRoot[r] = reasonsByRoot[r] || new Set()).add(kind); });
  return Object.keys(groups).filter((r) => groups[r].length > 1).map((r) => ({
    key: r,
    people: groups[r].slice().sort((a, b) => new Date(a.added_at) - new Date(b.added_at)),
    reasons: ['email', 'linkedin', 'github', 'name'].filter((k) => reasonsByRoot[r] && reasonsByRoot[r].has(k)).map((k) => REASONS[k]),
  }));
}

export function GtBenchDuplicates({ groups, bucketById, bucketColour, onClose, onMerge }) {
  const [ticked, setTicked] = useState(() => {
    const t = {};
    groups.forEach((g) => { t[g.key] = g.people.slice(0, 2).map((p) => p.candidate_id); });
    return t;
  });
  function toggle(gk, cid) {
    setTicked((t) => {
      const cur = t[gk] || [];
      return { ...t, [gk]: cur.indexOf(cid) !== -1 ? cur.filter((x) => x !== cid) : cur.concat([cid]) };
    });
  }
  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtbx-dupmodal" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">{groups.length === 1 ? 'One possible duplicate' : groups.length + ' possible duplicates'}</div>
        <div className="gtc-modal-body">
          <p className="gtbn-help">These people on the bench look like the same person. Tick two and merge them into one record. Merging is for admins, and it applies across the whole pool, not just this bench.</p>
          {groups.length === 0 ? <div className="gtr-empty-sm">No duplicates left on this bench.</div> : (
            <div className="gtbx-dups">
              {groups.map((g) => {
                const t = ticked[g.key] || [];
                return (
                  <div key={g.key} className="gtbx-dup">
                    <div className="gtbx-dup-head">
                      {g.reasons.map((r) => <span key={r} className="gtbx-reason">{r}</span>)}
                      <span className="gtbx-dup-n">{plural(g.people.length, 'record', 'records')}</span>
                    </div>
                    {g.people.map((p) => {
                      const on = t.indexOf(p.candidate_id) !== -1;
                      const b = bucketById[p.stage_id];
                      return (
                        <label key={p.id} className={'gtbx-duprow' + (on ? ' gtbx-duprow--on' : '')}>
                          <input type="checkbox" checked={on} onChange={() => toggle(g.key, p.candidate_id)} aria-label={'Tick ' + p.full_name} />
                          <span className="gtbx-av" style={{ background: avatarColour(p.full_name) }}>{initials(p.full_name)}</span>
                          <span className="gtbx-duprow-body">
                            <b>{p.full_name || 'Unnamed'} <span className="gtbx-ref">{p.ref}</span></b>
                            <span>{[p.email, p.current_title, p.current_company].filter(Boolean).join(' · ') || 'No contact details'}</span>
                          </span>
                          {b && <span className="gtbn-pill" style={{ background: bucketColour(b.tone).bg, color: bucketColour(b.tone).fg }}>{b.label}</span>}
                        </label>
                      );
                    })}
                    <div className="gtbx-dup-foot">
                      <span>{t.length === 2 ? 'Ready to merge' : 'Tick two to merge'}</span>
                      <button type="button" className="gtc-primary" disabled={t.length !== 2} onClick={() => onMerge(t.slice())}>Merge these two</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="gtc-modal-foot"><button type="button" className="gtc-btn-ghost" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

// ── A bench with no client: pick one before sharing ────────────────────────
export function GtBenchClientPick({ count, authFetch, flash, onClose, onPick }) {
  const [clientId, setClientId] = useState('');
  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head">Share {plural(count, 'person', 'people')} with a client</div>
        <div className="gtc-modal-body">
          <p className="gtbn-help">This bench has no client. Pick who the shortlist link is for. The bench itself is not changed.</p>
          <div className="gtc-field"><span>Client</span>
            <ClientField value={clientId} onChange={setClientId} authFetch={authFetch} flash={flash} placeholder="Choose a client" />
          </div>
        </div>
        <div className="gtc-modal-foot">
          <button type="button" className="gtc-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="gtc-primary" disabled={!clientId} onClick={() => onPick(clientId)}>Next</button>
        </div>
      </div>
    </div>
  );
}
